// src/screens/admin/SubAdminManagement.jsx
import { useContext, useEffect, useState } from "react";
import {
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiX,
  FiUser,
  FiMail,
  FiLock,
  FiSearch,
  FiFilter,
  FiShield,
} from "react-icons/fi";
import { Form, Spinner } from "react-bootstrap";
import "../../../assets/css/admin/screens/subAdminManagement/SubAdminManagement.css";
import AdminMasterLayout from "../../../layouts/admin/AdminMasterLayout";
import axios from "axios";
import AppContext from "../../../contexts/AppContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SubAdminManagement = () => {
  const { baseUrl, regExpChecker, adminAuthToken } = useContext(AppContext);

  const [showModal, setShowModal] = useState(false);
  const [editingSubAdmin, setEditingSubAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adminId, setAdminId] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [subAdmins, setSubAdmins] = useState([]);
  const [stats, setStats] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");

  const resetForm = () => {
    setAdminId(null);
    setName("");
    setEmail("");
    setPassword("");
    setIsSuperAdmin(false);
    setEditingSubAdmin(false);
    setErrors({});
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };
  const handleCreateClick = () => {
    resetForm();
    setEditingSubAdmin(false);
    setShowModal(true);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = "Name is required";
    }

    const emailError = regExpChecker("email", email);
    if (emailError) newErrors.email = emailError;

    const passwordError = regExpChecker("password", password);
    if (passwordError) newErrors.password = passwordError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const viewSubAdminStats = () => {
    const adminId = JSON.parse(localStorage.getItem("loggedInAdmin"))?.id;
    axios
      .get(`${baseUrl}/admin/SubAdmin/stats`, {
        headers: { Authorization: `Bearer ${adminAuthToken}` },
        params: { chadmin_id: adminId },
      })
      .then((res) => setStats(res.data))
      .catch((err) => console.log(err));
  };

  const createSubAdmin = () => {
    if (!validateForm()) return;

    setSaving(true);
    const fd = new FormData();
    fd.append("name", name);
    fd.append("email", email);
    fd.append("password", password);
    fd.append("isSuperAdmin", isSuperAdmin ? 1 : 0);
    axios
      .post(`${baseUrl}/admin/SubAdmin/createSubAdmin`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then(() => {
        toast.success("SubAdmin Created Successfully");
        viewAllSubAdmin();
        viewSubAdminStats();
        handleCloseModal();
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to create SubAdmin");
      })
      .finally(() => setSaving(false));
  };

  const updateSubAdmin = () => {
    if (!validateForm()) return;

    setSaving(true);
    const fd = new FormData();
    fd.append("adminId", adminId);
    fd.append("name", name);
    fd.append("email", email);
    fd.append("password", password);
    fd.append("isSuperAdmin", isSuperAdmin ? 1 : 0);
    axios
      .post(`${baseUrl}/admin/SubAdmin/updateSubAdmin`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then(() => {
        toast.info("SubAdmin Updated Successfully");
        viewAllSubAdmin();
        viewSubAdminStats();
        handleCloseModal();
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to update SubAdmin");
      })
      .finally(() => setSaving(false));
  };


  const viewAllSubAdmin = () => {
    setLoading(true);
    const fd = new FormData();
    axios
      .post(`${baseUrl}/admin/SubAdmin/viewAllSubAdmin`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then((res) => {
        setSubAdmins(res.data.subAdmins);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  const deleteSubAdmin = (id) => {
    const fd = new FormData();
    fd.append("adminId", id);
    axios
      .post(`${baseUrl}/admin/SubAdmin/deleteSubAdmin`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then(() => {
        toast.error("SubAdmin Deleted");
        viewAllSubAdmin();
        viewSubAdminStats();
      })
      .catch((err) => console.log(err));
  };

  const activateSubAdmin = (id) => {
    const fd = new FormData();
    fd.append("adminId", id);
    axios
      .post(`${baseUrl}/admin/SubAdmin/activateSubAdmin`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then(() => {
        toast.success("SubAdmin Activated");
        viewAllSubAdmin();
        viewSubAdminStats();
      })
      .catch((err) => console.log(err));
  };

  const deactivateSubAdmin = (id) => {
    const fd = new FormData();
    fd.append("adminId", id);
    axios
      .post(`${baseUrl}/admin/SubAdmin/deactivateSubAdmin`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then(() => {
        toast.warning("SubAdmin Deactivated");
        viewAllSubAdmin();
        viewSubAdminStats();
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    viewAllSubAdmin();
    viewSubAdminStats();
  }, []);

  const filteredSubAdmins = subAdmins.filter((admin) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      admin.name?.toLowerCase().includes(q) ||
      admin.email?.toLowerCase().includes(q) ||
      String(admin.id).includes(q);
    const matchesStatus =
      filterStatus === "All" ||
      (filterStatus === "Active" && admin.status == 1) ||
      (filterStatus === "Inactive" && admin.status != 1);
    return matchesSearch && matchesStatus;
  });

  return (
    <AdminMasterLayout>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="sad-card-shell">
        {/* ── Header ── */}
        <div className="sad-header-section">
          <div>
            <h3 className="sad-section-title">SubAdmin Management</h3>
            <p className="sad-section-subtitle">
              Manage your platform sub-administrators
            </p>
          </div>
          <button className="sad-create-btn" onClick={handleCreateClick}>
            <span>+</span> Add SubAdmin
          </button>
        </div>

        {/* ── Stat Cards ── */}
        <div className="sad-summary-grid">
          <div className="sad-summary-card">
            <div>
              <span className="sad-label">Total SubAdmins</span>
              <h3>{stats?.total_subadmins ?? "0"}</h3>
              <p className="sad-muted mb-0">All registered</p>
            </div>
            <div className="sad-card-icon">
              <FiUser size={22} />
            </div>
          </div>
          <div className="sad-summary-card">
            <div>
              <span className="sad-label">Active</span>
              <h3>{stats?.active_subadmins ?? "0"}</h3>
              <p className="sad-muted mb-0">Currently active</p>
            </div>
            <div className="sad-card-icon sad-card-icon--green">
              <FiCheckCircle size={22} />
            </div>
          </div>
          <div className="sad-summary-card">
            <div>
              <span className="sad-label">Inactive</span>
              <h3>{stats?.inactive_subadmins ?? "0"}</h3>
              <p className="sad-muted mb-0">Currently inactive</p>
            </div>
            <div className="sad-card-icon sad-card-icon--red">
              <FiXCircle size={22} />
            </div>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="sad-toolbar">
          <div className="sad-search-group">
            <span className="sad-search-icon">
              <FiSearch size={14} />
            </span>
            <input
              className="sad-search-input"
              placeholder="Search by name, email or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="sad-filter-group">
            <FiFilter size={14} />
            <select
              className="sad-filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="sad-layout">
          <div className="sad-table-wrapper">
            <table className="sad-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Super Admin</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <Spinner
                        animation="border"
                        size="sm"
                        className="sad-spinner"
                      />
                      <span className="ms-2 sad-muted sad-loading-text">
                        Loading...
                      </span>
                    </td>
                  </tr>
                )}

                {!loading &&
                  filteredSubAdmins.length > 0 &&
                  filteredSubAdmins.map((admin) => (
                    <tr key={admin.id}>
                      <td>
                        <span className="sad-id">#{admin.id}</span>
                      </td>
                      <td>
                        <div className="sad-user-cell">
                          <div className="sad-avatar">
                            {admin.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <span className="sad-user-name">{admin.name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="sad-email">{admin.email}</span>
                      </td>
                      <td>
                        <span
                          className={`sad-badge ${admin.isSuperAdmin ? "sad-badge--yes" : "sad-badge--no"}`}
                        >
                          {admin.isSuperAdmin ? "Yes" : "No"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`sad-status ${admin.status == 1 ? "sad-status--active" : "sad-status--inactive"}`}
                        >
                          {admin.status == 1 ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="sad-actions">
                          {/* Edit — hamesha */}
                          <button
                            className="sad-action-btn sad-action-btn--edit"
                            title="Edit"
                            onClick={() => {
                              setEditingSubAdmin(true);
                              setAdminId(admin.id);
                              setName(admin.name);
                              setEmail(admin.email);
                              setPassword(admin.password || "");
                              setIsSuperAdmin(admin.isSuperAdmin);
                              setErrors({});
                              setShowModal(true);
                            }}
                          >
                            <FiEdit2 size={13} />
                          </button>

                          {/* Activate — hamesha */}
                          <button
                            className="sad-action-btn sad-action-btn--activate"
                            title="Activate"
                            onClick={() => activateSubAdmin(admin.id)}
                          >
                            <FiCheckCircle size={13} />
                          </button>

                          {/* Deactivate — hamesha */}
                          <button
                            className="sad-action-btn sad-action-btn--deactivate"
                            title="Deactivate"
                            onClick={() => deactivateSubAdmin(admin.id)}
                          >
                            <FiXCircle size={13} />
                          </button>

                          {/* Delete — hamesha */}
                          <button
                            className="sad-action-btn sad-action-btn--delete"
                            title="Delete"
                            onClick={() => deleteSubAdmin(admin.id)}
                          >
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                {!loading && filteredSubAdmins.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="sad-empty-icon">👤</div>
                      <p className="sad-muted mt-2">
                        {subAdmins.length === 0
                          ? "No sub-admins found"
                          : "No sub-admins match the selected filters"}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Modal ── */}
      {showModal && (
        <div className="sad-modal-overlay" onClick={handleCloseModal}>
          <div
            className="sad-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sad-modal-header">
              <h4>{editingSubAdmin ? "Edit SubAdmin" : "Add New SubAdmin"}</h4>
              <button className="sad-modal-close" onClick={handleCloseModal}>
                <FiX size={20} />
              </button>
            </div>

            <div className="sad-modal-body">
              <Form>
                <Form.Group className="sad-form-group">
                  <label className="sad-form-label">
                    <FiUser size={14} /> Name{" "}
                    <span className="sad-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (errors.name)
                        setErrors((prev) => ({ ...prev, name: "" }));
                    }}
                    placeholder="e.g. John Doe"
                    className="sad-form-control"
                  />
                  {errors.name && (
                    <small className="sad-error">{errors.name}</small>
                  )}
                </Form.Group>

                <Form.Group className="sad-form-group">
                  <label className="sad-form-label">
                    <FiMail size={14} /> Email{" "}
                    <span className="sad-required">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email)
                        setErrors((prev) => ({ ...prev, email: "" }));
                    }}
                    placeholder="e.g. john@example.com"
                    className="sad-form-control"
                  />
                  {errors.email && (
                    <small className="sad-error">{errors.email}</small>
                  )}
                </Form.Group>

                <Form.Group className="sad-form-group">
                  <label className="sad-form-label">
                    <FiLock size={14} /> Password{" "}
                    <span className="sad-required">*</span>
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password)
                        setErrors((prev) => ({ ...prev, password: "" }));
                    }}
                    placeholder="Enter secure password"
                    className="sad-form-control"
                  />
                  {errors.password && (
                    <small className="sad-error">{errors.password}</small>
                  )}
                </Form.Group>

                <Form.Group className="sad-form-group">
                  <label className="sad-checkbox-row">
                    <input
                      type="checkbox"
                      checked={isSuperAdmin}
                      onChange={(e) => setIsSuperAdmin(e.target.checked)}
                    />
                    <span className="sad-checkbox-label">
                      <FiShield size={13} /> Is Super Admin
                    </span>
                  </label>
                </Form.Group>

                <div className="sad-modal-footer">
                  <button
                    type="button"
                    className="sad-btn-cancel"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="sad-btn-save"
                    disabled={saving}
                    onClick={() =>
                      editingSubAdmin ? updateSubAdmin() : createSubAdmin()
                    }
                  >
                    {saving
                      ? "Saving..."
                      : editingSubAdmin
                        ? "Update SubAdmin"
                        : "Add SubAdmin"}
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      )}
    </AdminMasterLayout>
  );
};

export default SubAdminManagement;